import { initTable } from '../../../../../ui/components/pure/list-table/list-table.init';
import { TableComponents } from '../../../../../ui/components/pure/list-table/list-table-cells.component';
import {
  COLUMN_WIDTHS,
  pinned,
  WRAP_ANYWHERE
} from '../../../../../ui/components/pure/list-table/list-table-column-styles';
import { useHasChildProjects } from '../../../../../hooks/use-has-child-projects.hook';
import dayjs from 'dayjs';
import { Delivery } from '../../../../deliveries/models/delivery.model';
import { MultipleNoteColumn } from '../../../../notes/components/multiple-note-column';
import { MultipleNotificationColumn } from '../../../../notifications/components/multiple-notification-column';

const FIXED_COLUMN_WIDTHS = {
  ...COLUMN_WIDTHS,
  age: '110px',
  /** Up to five 24px icons plus an overflow count. */
  iconList: '150px'
} as const;

export function useDeliveriesRequiringAttentionTable(projectID: string) {
  const hasChildProjects = useHasChildProjects(projectID);

  //Table initialization
  const tb = initTable<Delivery>();
  tb.styles({
    table: { tableLayout: 'fixed' }
  });
  tb.rowStyles({
    td: {
      height: '64px'
    }
  });

  //Desktop Columns
  tb.column('OD?', TableComponents.SVGCell, {
    SVG: (row) => row.requiresAttentionStatusIcon
  }).options({
    isSortable: true,
    sortId: 'state',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.icon),
    tooltip: 'delivery status'
  });
  tb.column('DELIVERED', TableComponents.BoldText, {
    value: (row) => {
      const targetDate = row.deliveredTime;
      const today = dayjs();
      const timeDiff = targetDate ? today.diff(targetDate) : 0;
      const minutesDiff = Math.ceil(timeDiff / (1000 * 60));
      const hoursDiff = Math.ceil(timeDiff / (1000 * 3600));
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      let displayText = '';

      if (minutesDiff < 60) {
        displayText = `${minutesDiff} minutes`;
      } else if (hoursDiff < 24) {
        displayText = `${hoursDiff} hours`;
      } else {
        displayText = `${daysDiff} day${daysDiff > 1 ? 's' : ''}`;
      }

      return displayText;
    }
  }).options({
    isSortable: true,
    sortId: 'deliveredTime',
    tooltip: 'day(s) since delivered',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.age)
  });
  tb.column('PROJECT', TableComponents.Text, {
    value: ':project.name'
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    styles: WRAP_ANYWHERE
  });
  tb.column('TYPE', TableComponents.SVGCell, {
    SVG: (row) => row.deliveryTypeIcon,
    iconSize: `~24px`
  }).options({
    isSortable: true,
    sortId: 'deliveryType',
    minBreakpoint: 'md',
    tooltip: 'delivery type',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.icon)
  });
  tb.column('TO', TableComponents.BoldText, {
    value: ':deliveredTo.party.name'
  }).options({
    isSortable: true,
    sortId: 'deliveredTo.party.name',
    styles: WRAP_ANYWHERE
  });
  tb.column('DELIVERED TIME', TableComponents.DateTime, {
    value: ':deliveredTime'
  }).options({
    isSortable: false,
    minBreakpoint: 'sm',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.dateTime)
  });
  tb.column('NOTIFICATIONS', MultipleNotificationColumn, {
    notifications: (row) => row.notifications,
    align: '~right',
    iconSize: `~24px`,
    maxVisibleNotifications: `~5`
  }).options({
    minBreakpoint: 'md',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.iconList)
  });
  tb.column('NOTES', MultipleNoteColumn, {
    notes: (row) => row.notes,
    align: '~right',
    iconSize: `~24px`,
    maxVisibleNotes: `~5`
  }).options({
    minBreakpoint: 'md',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.iconList)
  });

  tb.addPredicate('PROJECT', () => hasChildProjects);
  return tb;
}
